import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Clapperboard, Play, Lock, Unlock, Loader2, ChevronRight, ChevronDown, Cpu, Activity as ActivityIcon,
  Layers, GitBranch, Plug, History, Send, ShieldCheck, ArrowLeft, Gauge, CircleCheck, CircleAlert, RefreshCw,
  Globe2, Skull, Map as MapIcon, Swords, Sparkles, Waypoints, Users2, Music2, FlaskConical, Rocket, Boxes,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Spinner } from "@/components/Bits";
import { toast } from "sonner";
import { CharactersView, AudioFxView, PlaytestView, ShipView, EngineView } from "./studiopro/StageViews";

const STATUS_STYLE = {
  PASSED: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10",
  APPROVED: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10",
  GENERATED: "text-sky-400 border-sky-500/40 bg-sky-500/10",
  BUILDING: "text-sky-400 border-sky-500/40 bg-sky-500/10",
  PLANNED: "text-[hsl(var(--muted-foreground))] border-[hsl(var(--border))] bg-[hsl(var(--muted))]/40",
  LOCKED: "text-amber-400 border-amber-500/40 bg-amber-500/10",
  FAILED: "text-rose-400 border-rose-500/40 bg-rose-500/10",
  NEEDS_FIX: "text-rose-400 border-rose-500/40 bg-rose-500/10",
  GENERATING: "text-violet-400 border-violet-500/40 bg-violet-500/10 animate-pulse",
  TESTING: "text-violet-400 border-violet-500/40 bg-violet-500/10 animate-pulse",
  VALIDATING: "text-violet-400 border-violet-500/40 bg-violet-500/10 animate-pulse",
  OUTDATED: "text-orange-400 border-orange-500/40 bg-orange-500/10",
};
const AGENT_EMOJI = {
  game_director: "🎬", game_designer: "🎯", level_designer: "🗺️", world_builder: "🌍",
  gameplay_programmer: "🎮", ai_programmer: "🤖", environment_artist: "🏙️", audio_director: "🎧",
  animation_director: "🎞️", cinematic_director: "🎥", qa_agent: "🧪",
};
const JOB_LABEL = {
  bootstrap: "Building your vertical slice", command: "Applying your change",
  world: "Building the procedural world", mission: "Generating a mission",
  enemies: "Designing enemy systems", characters: "Designing the cast",
  animation: "Designing the animation system", audio: "Composing audio & music",
  cinematics: "Staging cinematics", vfx: "Designing visual effects",
  playtest: "Running the AI playtest", polish: "Auto-polishing the build",
  package: "Packaging the release", produce: "🎬 Producing your whole game",
};

const NAV = [
  { key: "overview", label: "Overview", icon: Gauge },
  { key: "dna", label: "Game DNA", icon: Cpu },
  { key: "graph", label: "Production Graph", icon: GitBranch },
  { key: "engine", label: "Engine", icon: Boxes },
  { key: "world", label: "World & Gameplay", icon: Globe2 },
  { key: "characters", label: "Characters", icon: Users2 },
  { key: "audiofx", label: "Audio & FX", icon: Music2 },
  { key: "playtest", label: "Playtest & Polish", icon: FlaskConical },
  { key: "ship", label: "Ship & Export", icon: Rocket },
  { key: "agents", label: "AI Workforce", icon: ActivityIcon },
  { key: "providers", label: "Providers", icon: Plug },
  { key: "versions", label: "Versions", icon: History },
];
const PROV_STATUS = {
  READY: "text-emerald-400 border-emerald-500/40", CONNECTED: "text-emerald-400 border-emerald-500/40",
  CONFIGURATION_REQUIRED: "text-amber-400 border-amber-500/40", NOT_CONNECTED: "text-[hsl(var(--muted-foreground))] border-[hsl(var(--border))]",
  ERROR: "text-rose-400 border-rose-500/40",
};

const StatusChip = ({ status }) => (
  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded border ${STATUS_STYLE[status] || STATUS_STYLE.PLANNED}`}>{status}</span>
);

export default function StudioProProject() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [project, setProject] = useState(null);
  const [providers, setProviders] = useState([]);
  const [view, setView] = useState("overview");
  const [selNode, setSelNode] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [command, setCommand] = useState("");
  const [sending, setSending] = useState(false);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/gv2/projects/${projectId}`); setProject(data); return data; }
    catch { return null; }
  }, [projectId]);

  useEffect(() => {
    load();
    api.get("/gv2/providers").then(({ data }) => setProviders(data.providers || [])).catch(() => {});
  }, [load]);

  // Live polling while a job is active (survives the durable backend job).
  useEffect(() => {
    const tick = async () => {
      const d = await load();
      const active = d?.active_job;
      clearTimeout(pollRef.current);
      pollRef.current = setTimeout(tick, active ? 2500 : 9000);
    };
    pollRef.current = setTimeout(tick, 2500);
    return () => clearTimeout(pollRef.current);
  }, [load]);

  const job = project?.active_job;
  const lastJob = project?.latest_job;

  const sendCommand = async () => {
    if (command.trim().length < 2 || job) return;
    setSending(true);
    try {
      await api.post(`/gv2/projects/${projectId}/command`, { command: command.trim() });
      toast.success("Change Impact Analyser is scoping your request…");
      setCommand("");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Command failed."); }
    finally { setSending(false); }
  };

  const toggleLock = async (node) => {
    try {
      await api.post(`/gv2/projects/${projectId}/nodes/${node.id}/lock`, { locked: !node.locked });
      toast.success(!node.locked ? `${node.id} locked — AI won't change it.` : `${node.id} unlocked.`);
      const d = await load(); if (selNode) setSelNode((d.nodes || []).find((n) => n.id === selNode.id) || null);
    } catch { toast.error("Couldn't change the lock."); }
  };

  const restore = async (v) => {
    try { await api.post(`/gv2/projects/${projectId}/restore/${v}`); toast.success(`Restored to v${v}.`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!project) return <div className="p-8"><Spinner /></div>;

  const nodes = project.nodes || [];
  const structural = nodes.filter((n) => ["GAME", "WORLD", "REGION", "CHAPTER", "MISSION", "SECTION"].includes(n.type));
  const play = () => project.game_id ? navigate(`/app/build/${project.game_id}`) : toast.error("No playable build yet.");

  return (
    <div className="flex flex-col min-h-full" data-testid="studio-pro-workspace">
      {/* Top bar */}
      <div className="flex items-center gap-3 px-4 lg:px-6 py-3 border-b border-[hsl(var(--border))] bg-[hsl(var(--card))]/60 backdrop-blur sticky top-0 z-20">
        <button onClick={() => navigate("/app/studio-pro")} className="p-1.5 rounded-lg hover:bg-[hsl(var(--accent))]" data-testid="gv2-back-btn"><ArrowLeft className="w-4 h-4" /></button>
        <Clapperboard className="w-5 h-5 text-violet-400" />
        <div className="min-w-0">
          <div className="font-bold truncate leading-none">{project.name}</div>
          <div className="text-[11px] text-[hsl(var(--muted-foreground))]">{project.dna?.identity?.genre || "Designing…"} · {project.engine === "unreal" ? "Unreal" : "Web Runtime"}</div>
        </div>
        <Badge variant="outline" className="ml-1 hidden sm:flex text-[10px]">{project.build?.percent || 0}% built</Badge>
        <div className="ml-auto flex items-center gap-2">
          <Button size="sm" onClick={play} disabled={!project.game_id} className="rounded-full gap-1.5 bg-gradient-to-r from-cyan-500 to-violet-600" data-testid="gv2-play-btn"><Play className="w-4 h-4" /> Play Slice</Button>
        </div>
      </div>

      {/* Job progress banner */}
      {job && (
        <div className="px-4 lg:px-6 py-2.5 bg-violet-500/10 border-b border-violet-500/20" data-testid="gv2-job-banner">
          <div className="flex items-center gap-2 text-sm font-semibold text-violet-300">
            <Loader2 className="w-4 h-4 animate-spin" />
            {JOB_LABEL[job.type] || "Working"} — {job.progress}%
          </div>
          <div className="mt-1.5 h-1.5 rounded-full bg-[hsl(var(--secondary))] overflow-hidden">
            <div className="h-full bg-gradient-to-r from-cyan-500 to-violet-600 transition-all" style={{ width: `${job.progress}%` }} />
          </div>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {job.steps.map((s) => (
              <span key={s.key} className={`text-[10px] px-1.5 py-0.5 rounded border flex items-center gap-1 ${s.status === "done" ? "text-emerald-400 border-emerald-500/40" : s.status === "running" ? "text-violet-300 border-violet-500/40 animate-pulse" : s.status === "failed" ? "text-rose-400 border-rose-500/40" : "text-[hsl(var(--muted-foreground))] border-[hsl(var(--border))]"}`} data-testid={`gv2-step-${s.key}`}>
                {s.status === "done" && <CircleCheck className="w-3 h-3" />}{s.status === "failed" && <CircleAlert className="w-3 h-3" />}{s.title.split("—")[0].trim()}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-1 min-h-[60vh]">
        {/* Left nav */}
        <div className="w-14 sm:w-48 shrink-0 border-r border-[hsl(var(--border))] bg-[hsl(var(--card))]/30 py-3">
          {NAV.map((n) => {
            const Icon = n.icon; const active = view === n.key;
            return (
              <button key={n.key} onClick={() => { setView(n.key); setSelNode(null); }} data-testid={`gv2-nav-${n.key}`}
                className={`w-full flex items-center gap-2.5 px-3 sm:px-4 py-2.5 text-sm transition-colors ${active ? "text-violet-400 bg-violet-500/10 border-r-2 border-violet-500 font-semibold" : "text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--accent))]"}`}>
                <Icon className="w-4 h-4 shrink-0" /><span className="hidden sm:inline">{n.label}</span>
              </button>
            );
          })}
        </div>

        {/* Center */}
        <div className="flex-1 min-w-0 p-4 lg:p-6">
          {view === "overview" && <Overview project={project} projectId={projectId} job={job} play={play} lastJob={lastJob} reload={load} />}
          {view === "dna" && <DNAView dna={project.dna} />}
          {view === "graph" && <GraphView structural={structural} nodes={nodes} expanded={expanded} setExpanded={setExpanded} selNode={selNode} setSelNode={setSelNode} toggleLock={toggleLock} />}
          {view === "engine" && <EngineView project={project} projectId={projectId} job={job} reload={load} providers={providers} />}
          {view === "world" && <WorldGameplayView project={project} projectId={projectId} nodes={nodes} job={job} reload={load} />}
          {view === "characters" && <CharactersView project={project} projectId={projectId} nodes={nodes} job={job} reload={load} />}
          {view === "audiofx" && <AudioFxView project={project} projectId={projectId} nodes={nodes} job={job} reload={load} />}
          {view === "playtest" && <PlaytestView project={project} projectId={projectId} nodes={nodes} job={job} reload={load} />}
          {view === "ship" && <ShipView project={project} projectId={projectId} nodes={nodes} job={job} reload={load} />}
          {view === "agents" && <AgentsView activity={project.activity || []} />}
          {view === "providers" && <ProvidersView providers={providers} />}
          {view === "versions" && <VersionsView versions={project.versions || []} restore={restore} />}
        </div>

        {/* Right rail */}
        <div className="w-72 shrink-0 border-l border-[hsl(var(--border))] bg-[hsl(var(--card))]/30 hidden lg:block">
          {selNode ? (
            <NodeProperties node={selNode} toggleLock={toggleLock} onClose={() => setSelNode(null)} />
          ) : (
            <div className="p-4">
              <div className="text-xs font-bold uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-3 flex items-center gap-1.5"><ActivityIcon className="w-3.5 h-3.5" /> Agent Activity</div>
              <div className="space-y-2" data-testid="gv2-activity-rail">
                {(project.activity || []).slice(-14).reverse().map((a) => (
                  <div key={a.id} className="text-xs flex items-start gap-2">
                    <span className="text-base leading-none">{AGENT_EMOJI[a.agent] || "🛠️"}</span>
                    <div className="min-w-0">
                      <div className="font-semibold truncate">{a.action}</div>
                      {a.detail && <div className="text-[hsl(var(--muted-foreground))] truncate">{a.detail}</div>}
                    </div>
                  </div>
                ))}
                {(project.activity || []).length === 0 && <div className="text-xs text-[hsl(var(--muted-foreground))]">The workforce log will appear here.</div>}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Command Centre */}
      <div className="border-t border-[hsl(var(--border))] bg-[hsl(var(--card))]/80 backdrop-blur px-4 lg:px-6 py-3 sticky bottom-0 z-20" data-testid="gv2-command-centre">
        <div className="flex items-center gap-2 max-w-4xl mx-auto">
          <div className="flex items-center gap-1.5 text-[11px] text-[hsl(var(--muted-foreground))] shrink-0">
            <Cpu className="w-3.5 h-3.5 text-violet-400" /><span className="hidden sm:inline">Command{selNode ? ` · ${selNode.id}` : ""}</span>
          </div>
          <input
            value={command} onChange={(e) => setCommand(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") sendCommand(); }}
            disabled={!!job || sending}
            placeholder={job ? "Working…" : selNode ? `Change ${selNode.id} — e.g. make it bigger and darker…` : "Tell the studio what to change — e.g. double the city street section and add an enemy squad…"}
            data-testid="gv2-command-input"
            className="flex-1 rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/40 disabled:opacity-60"
          />
          <Button onClick={sendCommand} disabled={!!job || sending || command.trim().length < 2} data-testid="gv2-command-send"
            className="rounded-full gap-1.5 bg-gradient-to-r from-cyan-500 to-violet-600">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ── Views ── */
function Overview({ project, projectId, job, play, lastJob, reload }) {
  const d = project.dna?.identity || {};
  const gp = project.dna?.gameplay || {};
  const [producing, setProducing] = useState(false);
  const produceJob = job?.type === "produce" ? job : null;

  const produce = async () => {
    if (job || producing) return;
    setProducing(true);
    try {
      await api.post(`/gv2/projects/${projectId}/produce`);
      toast.success("🎬 Full production started — the whole studio is on it!");
      await reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't start production.");
    } finally { setProducing(false); }
  };

  return (
    <div className="max-w-3xl" data-testid="gv2-overview">
      <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5 mb-4">
        <div className="flex items-center gap-2 mb-1"><ShieldCheck className="w-4 h-4 text-violet-400" /><span className="text-xs font-bold uppercase tracking-wide text-[hsl(var(--muted-foreground))]">Concept</span></div>
        <h2 className="font-display text-2xl font-extrabold">{d.name || project.name}</h2>
        <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">{d.player_experience || project.idea}</p>
        <div className="flex flex-wrap gap-2 mt-3">
          {d.genre && <Badge variant="outline">{d.genre}</Badge>}
          {d.perspective && <Badge variant="outline">{d.perspective}</Badge>}
          {d.tone && <Badge variant="outline">{d.tone}</Badge>}
          <Badge variant="outline" className="capitalize">{d.quality_target || project.quality}</Badge>
        </div>
      </div>

      {/* One-Tap Full Production */}
      <div className="rounded-2xl border border-violet-500/30 bg-gradient-to-br from-violet-500/10 to-cyan-500/5 p-5 mb-4" data-testid="gv2-produce-card">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="font-display text-lg font-extrabold flex items-center gap-2"><Clapperboard className="w-5 h-5 text-violet-400" /> One-Tap Full Production</div>
            <p className="text-sm text-[hsl(var(--muted-foreground))] mt-0.5">Run the entire studio end-to-end — world, missions, enemies, cast, animation, audio, cinematics, VFX, an AI playtest and a packaged release — in a single resumable job.</p>
          </div>
          <Button onClick={produce} disabled={!!job || producing || !project.game_id} data-testid="gv2-produce-btn"
            className="rounded-full gap-1.5 bg-gradient-to-r from-violet-600 to-cyan-500 shrink-0">
            {producing || produceJob ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {produceJob ? "Producing…" : "Produce my game"}
          </Button>
        </div>

        {produceJob && (
          <div className="mt-4 rounded-xl border border-violet-500/20 bg-[hsl(var(--background))]/60 p-3" data-testid="gv2-mission-control">
            <div className="text-[11px] font-bold uppercase tracking-wide text-violet-300 mb-2">Mission control · {produceJob.progress}%</div>
            <div className="space-y-1.5">
              {produceJob.steps.map((s) => (
                <div key={s.key} className="flex items-center gap-2 text-xs" data-testid={`gv2-mc-step-${s.key}`}>
                  {s.status === "done" ? <CircleCheck className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                    : s.status === "running" ? <Loader2 className="w-3.5 h-3.5 text-violet-300 animate-spin shrink-0" />
                    : s.status === "failed" ? <CircleAlert className="w-3.5 h-3.5 text-rose-400 shrink-0" />
                    : <span className="w-3.5 h-3.5 rounded-full border border-[hsl(var(--border))] shrink-0" />}
                  <span className={`flex-1 truncate ${s.status === "running" ? "text-violet-200 font-semibold" : s.status === "pending" ? "text-[hsl(var(--muted-foreground))]" : ""}`}>{s.title}</span>
                  {s.detail && <span className="text-[10px] text-[hsl(var(--muted-foreground))] truncate max-w-[40%]">{s.detail}</span>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="grid sm:grid-cols-3 gap-3 mb-4">
        <Stat label="Build progress" value={`${project.build?.percent || 0}%`} />
        <Stat label="Graph nodes" value={project.build?.total_nodes || 0} />
        <Stat label="Playable" value={project.game_id ? "Yes" : "Building…"} />
      </div>
      {gp.primary_loop && (
        <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4 mb-4">
          <div className="text-xs font-bold uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-1">Core loop</div>
          <p className="text-sm">{gp.primary_loop}</p>
        </div>
      )}
      <Button onClick={play} disabled={!project.game_id} className="rounded-full gap-2 bg-gradient-to-r from-cyan-500 to-violet-600"><Play className="w-4 h-4" /> Play the vertical slice</Button>
      {lastJob?.status === "failed" && <p className="text-xs text-rose-400 mt-3">Last job failed: {lastJob.error}</p>}
    </div>
  );
}
const Stat = ({ label, value }) => (
  <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4">
    <div className="text-[11px] text-[hsl(var(--muted-foreground))]">{label}</div>
    <div className="text-xl font-bold">{value}</div>
  </div>
);

function DNAView({ dna }) {
  if (!dna) return null;
  const render = (v) => {
    if (Array.isArray(v)) return v.map((x, i) => <span key={i} className="inline-block text-xs bg-[hsl(var(--muted))]/50 rounded px-1.5 py-0.5 mr-1 mb-1">{typeof x === "object" ? (x.name || x.title || JSON.stringify(x)) : String(x)}</span>);
    if (v && typeof v === "object") return Object.entries(v).map(([k, val]) => val ? <div key={k} className="text-xs"><span className="text-[hsl(var(--muted-foreground))]">{k}:</span> {typeof val === "object" ? JSON.stringify(val) : String(val)}</div> : null);
    return <span className="text-sm">{String(v || "—")}</span>;
  };
  return (
    <div className="max-w-3xl grid sm:grid-cols-2 gap-3" data-testid="gv2-dna-view">
      {Object.entries(dna).map(([section, val]) => (
        <div key={section} className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4">
          <div className="text-xs font-bold uppercase tracking-wide text-violet-400 mb-2">{section}</div>
          <div className="space-y-0.5">{render(val)}</div>
        </div>
      ))}
    </div>
  );
}

function GraphView({ structural, nodes, expanded, setExpanded, selNode, setSelNode, toggleLock }) {
  const children = (id) => nodes.filter((n) => n.parent === id);
  const renderRow = (n, depth) => {
    const kids = children(n.id);
    const isOpen = expanded[n.id] !== false;
    const isStruct = ["GAME", "WORLD", "REGION", "CHAPTER", "MISSION", "SECTION"].includes(n.type);
    return (
      <div key={n.id}>
        <div className={`flex items-center gap-2 rounded-lg px-2 py-1.5 cursor-pointer hover:bg-[hsl(var(--accent))] ${selNode?.id === n.id ? "bg-violet-500/10 ring-1 ring-violet-500/40" : ""}`}
          style={{ paddingLeft: `${depth * 14 + 8}px` }} onClick={() => setSelNode(n)} data-testid={`gv2-node-${n.id}`}>
          {kids.length > 0 ? (
            <button onClick={(e) => { e.stopPropagation(); setExpanded((x) => ({ ...x, [n.id]: !isOpen })); }} className="p-0.5">
              {isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
            </button>
          ) : <span className="w-4" />}
          <span className="text-[10px] font-mono text-[hsl(var(--muted-foreground))] shrink-0">{n.id}</span>
          <span className={`text-sm truncate flex-1 ${isStruct ? "font-semibold" : ""}`}>{n.title}</span>
          {n.locked && <Lock className="w-3 h-3 text-amber-400" />}
          <StatusChip status={n.status} />
        </div>
        {isOpen && kids.map((k) => renderRow(k, depth + 1))}
      </div>
    );
  };
  const roots = structural.filter((n) => !n.parent || !nodes.find((p) => p.id === n.parent));
  return (
    <div className="max-w-3xl" data-testid="gv2-graph-view">
      <p className="text-sm text-[hsl(var(--muted-foreground))] mb-3">Every node has a persistent ID and build state. Select a node to inspect it or lock it so the AI never changes it.</p>
      <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-2">
        {roots.length === 0 ? <div className="p-4 text-sm text-[hsl(var(--muted-foreground))]">Graph is generating…</div> : roots.map((r) => renderRow(r, 0))}
      </div>
    </div>
  );
}

function AgentsView({ activity }) {
  return (
    <div className="max-w-3xl" data-testid="gv2-agents-view">
      <p className="text-sm text-[hsl(var(--muted-foreground))] mb-3">The AI workforce — each specialist has a defined role, reads the Game DNA, works on Production Graph nodes, and hands off to the next specialist.</p>
      <div className="space-y-2">
        {activity.slice().reverse().map((a) => (
          <div key={a.id} className="flex items-start gap-3 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 py-2.5" data-testid={`gv2-activity-${a.id}`}>
            <span className="text-xl leading-none">{AGENT_EMOJI[a.agent] || "🛠️"}</span>
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-sm">{a.action}</div>
              {a.detail && <div className="text-xs text-[hsl(var(--muted-foreground))]">{a.detail}</div>}
            </div>
            {a.node && <Badge variant="outline" className="text-[10px] font-mono">{a.node}</Badge>}
            {a.status === "needs_fix" && <CircleAlert className="w-4 h-4 text-rose-400" />}
          </div>
        ))}
        {activity.length === 0 && <div className="text-sm text-[hsl(var(--muted-foreground))]">No activity yet.</div>}
      </div>
    </div>
  );
}

function ProvidersView({ providers }) {
  const cats = [...new Set(providers.map((p) => p.category))];
  return (
    <div className="max-w-3xl" data-testid="gv2-providers-view">
      <p className="text-sm text-[hsl(var(--muted-foreground))] mb-3">GameView is engine-agnostic. Every capability is a swappable provider — connect real Unreal/GPU infrastructure later without redesigning anything.</p>
      {cats.map((cat) => (
        <div key={cat} className="mb-4">
          <div className="text-xs font-bold uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-2">{cat}</div>
          <div className="space-y-2">
            {providers.filter((p) => p.category === cat).map((p) => (
              <div key={p.key} className="flex items-center gap-3 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 py-2.5" data-testid={`gv2-provider-${p.key}`}>
                <Plug className="w-4 h-4 text-[hsl(var(--muted-foreground))]" />
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-sm truncate">{p.name}</div>
                  <div className="text-[11px] text-[hsl(var(--muted-foreground))] truncate">{p.note}</div>
                </div>
                <Badge variant="outline" className={`text-[10px] shrink-0 ${PROV_STATUS[p.status] || ""}`}>{p.status.replace(/_/g, " ")}</Badge>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function VersionsView({ versions, restore }) {
  return (
    <div className="max-w-2xl" data-testid="gv2-versions-view">
      <p className="text-sm text-[hsl(var(--muted-foreground))] mb-3">Every major change is a restorable version. Nothing is ever destroyed.</p>
      <div className="space-y-2">
        {versions.slice().reverse().map((v, i) => (
          <div key={i} className="flex items-center gap-3 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 py-2.5" data-testid={`gv2-version-${v.v}`}>
            <Badge variant="outline" className="font-mono text-xs">v{v.v}</Badge>
            <span className="text-sm truncate flex-1">{v.label}</span>
          </div>
        ))}
        {versions.length === 0 && <div className="text-sm text-[hsl(var(--muted-foreground))]">No versions yet.</div>}
      </div>
    </div>
  );
}

function NodeProperties({ node, toggleLock, onClose }) {
  return (
    <div className="p-4" data-testid="gv2-node-props">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs font-bold uppercase tracking-wide text-[hsl(var(--muted-foreground))]">Node</div>
        <button onClick={onClose} className="text-xs text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]">✕</button>
      </div>
      <div className="font-mono text-xs text-violet-400 mb-1">{node.id}</div>
      <div className="font-semibold mb-2">{node.title}</div>
      <div className="flex items-center gap-2 mb-3"><StatusChip status={node.status} /><Badge variant="outline" className="text-[10px]">{node.type}</Badge><span className="text-[11px] text-[hsl(var(--muted-foreground))]">v{node.version}</span></div>
      {node.agent && <div className="text-xs mb-1"><span className="text-[hsl(var(--muted-foreground))]">Owner:</span> {AGENT_EMOJI[node.agent] || ""} {node.agent.replace(/_/g, " ")}</div>}
      {node.provider && <div className="text-xs mb-1"><span className="text-[hsl(var(--muted-foreground))]">Provider:</span> {node.provider}</div>}
      {node.dependencies?.length > 0 && <div className="text-xs mb-2"><span className="text-[hsl(var(--muted-foreground))]">Dependencies:</span> {node.dependencies.length}</div>}
      {node.spec && Object.keys(node.spec).length > 0 && (
        <div className="mt-2 rounded-lg bg-[hsl(var(--muted))]/40 p-2 text-[11px] space-y-1">
          {Object.entries(node.spec).map(([k, v]) => <div key={k}><span className="text-[hsl(var(--muted-foreground))]">{k}:</span> {typeof v === "object" ? JSON.stringify(v) : String(v)}</div>)}
        </div>
      )}
      {node.tests?.length > 0 && (
        <div className="mt-3">
          <div className="text-[11px] font-bold text-[hsl(var(--muted-foreground))] mb-1">Playtest</div>
          {node.tests.map((t, i) => <div key={i} className="text-[11px] flex items-center gap-1">{t.status === "PASSED" ? <CircleCheck className="w-3 h-3 text-emerald-400" /> : <CircleAlert className="w-3 h-3 text-rose-400" />}{t.name}</div>)}
        </div>
      )}
      <Button size="sm" variant={node.locked ? "default" : "outline"} onClick={() => toggleLock(node)} data-testid="gv2-node-lock-btn"
        className="rounded-full gap-1.5 mt-4 w-full">
        {node.locked ? <><Unlock className="w-3.5 h-3.5" /> Unlock node</> : <><Lock className="w-3.5 h-3.5" /> Lock node</>}
      </Button>
    </div>
  );
}

/* ── Phase 2 — World & Gameplay ── */
const TIER_STYLE = {
  1: "border-emerald-500/40 text-emerald-400", 2: "border-sky-500/40 text-sky-400",
  3: "border-amber-500/40 text-amber-400", 4: "border-orange-500/40 text-orange-400",
  5: "border-rose-500/40 text-rose-400",
};
const ARCHETYPE_EMOJI = {
  grunt: "🪖", flanker: "🌀", sniper: "🎯", heavy: "🛡️", drone: "🛰️",
  brute: "👹", support: "➕", elite: "⭐", boss: "👑",
};

function WorldGameplayView({ project, projectId, nodes, job, reload }) {
  const [sub, setSub] = useState("world");
  const [templates, setTemplates] = useState([]);
  const [selTpl, setSelTpl] = useState(null);
  const [brief, setBrief] = useState("");
  const [enemyBrief, setEnemyBrief] = useState("");
  const [busy, setBusy] = useState("");

  useEffect(() => {
    api.get("/gv2/mission-templates").then(({ data }) => setTemplates(data.templates || [])).catch(() => {});
  }, []);

  const regions = nodes.filter((n) => n.type === "REGION");
  const missions = nodes.filter((n) => n.type === "MISSION");
  const enemies = nodes.filter((n) => n.type === "ENEMY" && n.spec?.archetype);
  const behaviourOf = (enemyId) => nodes.find((n) => n.type === "BEHAVIOUR" && n.parent === enemyId);
  const disabled = !!job || !!busy;

  const run = async (kind, path, bodyObj, msg) => {
    if (disabled) return;
    setBusy(kind);
    try {
      await api.post(path, bodyObj || {});
      toast.success(msg);
      await reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't start that.");
    } finally { setBusy(""); }
  };

  const SUBS = [
    { key: "world", label: "World Map", icon: MapIcon },
    { key: "missions", label: "Missions", icon: Waypoints },
    { key: "enemies", label: "Enemy Systems", icon: Skull },
  ];

  return (
    <div className="max-w-3xl" data-testid="gv2-world-view">
      <p className="text-sm text-[hsl(var(--muted-foreground))] mb-3">
        Phase 2 — the AI workforce builds the playable world & gameplay: a procedural multi-region world,
        template-driven missions with level layouts, and enemy archetypes with real behaviour trees.
      </p>
      <div className="flex gap-1.5 mb-4 flex-wrap">
        {SUBS.map((s) => {
          const Icon = s.icon; const active = sub === s.key;
          return (
            <button key={s.key} onClick={() => setSub(s.key)} data-testid={`gv2-world-tab-${s.key}`}
              className={`flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm border transition-colors ${active ? "border-violet-500 bg-violet-500/10 text-violet-300 font-semibold" : "border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:border-violet-500/40"}`}>
              <Icon className="w-3.5 h-3.5" /> {s.label}
            </button>
          );
        })}
      </div>

      {/* World Map */}
      {sub === "world" && (
        <div data-testid="gv2-world-map">
          <div className="flex items-center justify-between mb-3 gap-3">
            <div>
              <div className="font-semibold flex items-center gap-1.5"><Globe2 className="w-4 h-4 text-cyan-400" /> Procedural World</div>
              <div className="text-xs text-[hsl(var(--muted-foreground))]">{regions.length} region{regions.length === 1 ? "" : "s"} generated</div>
            </div>
            <Button size="sm" disabled={disabled} onClick={() => run("world", `/gv2/projects/${projectId}/world`, {}, "World Builder is generating your world…")}
              data-testid="gv2-generate-world-btn" className="rounded-full gap-1.5 bg-gradient-to-r from-cyan-500 to-violet-600">
              {busy === "world" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {regions.length ? "Expand world" : "Generate world"}
            </Button>
          </div>
          {regions.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-[hsl(var(--border))] p-8 text-center text-sm text-[hsl(var(--muted-foreground))]">
              No regions yet. Run the PCG World Builder to design a connected multi-region world with biomes, hazards and points of interest.
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-3">
              {regions.map((r) => (
                <div key={r.id} className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4" data-testid={`gv2-region-${r.id}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-bold truncate flex-1">{r.title}</span>
                    <Badge variant="outline" className={`text-[10px] ${TIER_STYLE[r.spec?.tier] || ""}`}>Tier {r.spec?.tier || 1}</Badge>
                  </div>
                  <div className="text-xs text-[hsl(var(--muted-foreground))] mb-2">{r.spec?.biome}{r.spec?.terrain ? ` · ${r.spec.terrain}` : ""}</div>
                  {r.spec?.summary && <p className="text-xs mb-2">{r.spec.summary}</p>}
                  {r.spec?.hazards?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-2">
                      {r.spec.hazards.slice(0, 4).map((h, i) => <span key={i} className="text-[10px] bg-rose-500/10 text-rose-300 rounded px-1.5 py-0.5">⚠ {String(h)}</span>)}
                    </div>
                  )}
                  {r.spec?.connections?.length > 0 && (
                    <div className="text-[11px] text-[hsl(var(--muted-foreground))] flex items-center gap-1"><Waypoints className="w-3 h-3" /> {r.spec.connections.slice(0, 3).join(", ")}</div>
                  )}
                  <div className="text-[11px] text-cyan-400 mt-1">{nodes.filter((n) => n.type === "POI" && n.parent === r.id).length} points of interest</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Missions */}
      {sub === "missions" && (
        <div data-testid="gv2-mission-builder">
          <div className="font-semibold flex items-center gap-1.5 mb-1"><Waypoints className="w-4 h-4 text-violet-400" /> Mission Templates</div>
          <div className="text-xs text-[hsl(var(--muted-foreground))] mb-3">Pick an archetype — the Level Designer instantiates a full mission with a spatial level layout.</div>
          <div className="grid sm:grid-cols-2 gap-2 mb-3">
            {templates.map((t) => (
              <button key={t.id} onClick={() => setSelTpl(t.id === selTpl ? null : t.id)} data-testid={`gv2-template-${t.id}`}
                className={`text-left rounded-xl border px-3 py-2.5 transition-colors ${selTpl === t.id ? "border-violet-500 bg-violet-500/10" : "border-[hsl(var(--border))] hover:border-violet-500/40"}`}>
                <div className="font-semibold text-sm flex items-center gap-1.5"><span className="text-base">{t.emoji}</span>{t.name}</div>
                <div className="text-[11px] text-[hsl(var(--muted-foreground))]">{t.summary}</div>
              </button>
            ))}
          </div>
          {selTpl && (
            <div className="rounded-2xl border border-violet-500/30 bg-violet-500/5 p-3 mb-4">
              <textarea value={brief} onChange={(e) => setBrief(e.target.value)} rows={2}
                placeholder="Optional brief — e.g. set it in the flooded lower district, add a mid-mission ambush…"
                data-testid="gv2-mission-brief"
                className="w-full rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-violet-500/40" />
              <Button size="sm" disabled={disabled} data-testid="gv2-generate-mission-btn"
                onClick={() => run("mission", `/gv2/projects/${projectId}/missions`, { template_id: selTpl, brief }, "Level Designer is building your mission…")}
                className="rounded-full gap-1.5 mt-2 bg-gradient-to-r from-cyan-500 to-violet-600">
                {busy === "mission" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Generate mission
              </Button>
            </div>
          )}
          <div className="font-semibold text-sm mb-2">Missions in this game ({missions.length})</div>
          <div className="space-y-2">
            {missions.map((m) => (
              <div key={m.id} className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 py-2.5" data-testid={`gv2-mission-card-${m.id}`}>
                <div className="flex items-center gap-2">
                  <Waypoints className="w-4 h-4 text-violet-400 shrink-0" />
                  <span className="font-semibold text-sm truncate flex-1">{m.title}</span>
                  {m.spec?.template_name && <Badge variant="outline" className="text-[10px]">{m.spec.template_name}</Badge>}
                </div>
                <div className="text-[11px] text-[hsl(var(--muted-foreground))] mt-1">
                  {nodes.filter((n) => n.type === "SECTION" && n.parent === m.id).length} sections
                  {nodes.find((n) => n.type === "LEVEL" && n.parent === m.id) ? " · level layout ✓" : ""}
                </div>
              </div>
            ))}
            {missions.length === 0 && <div className="text-sm text-[hsl(var(--muted-foreground))]">No missions yet.</div>}
          </div>
        </div>
      )}

      {/* Enemy Systems */}
      {sub === "enemies" && (
        <div data-testid="gv2-enemy-systems">
          <div className="flex items-center justify-between mb-3 gap-3">
            <div>
              <div className="font-semibold flex items-center gap-1.5"><Skull className="w-4 h-4 text-rose-400" /> Enemy Systems</div>
              <div className="text-xs text-[hsl(var(--muted-foreground))]">{enemies.length} archetype{enemies.length === 1 ? "" : "s"} with behaviour trees</div>
            </div>
            <Button size="sm" disabled={disabled} data-testid="gv2-generate-enemies-btn"
              onClick={() => run("enemies", `/gv2/projects/${projectId}/enemies`, { brief: enemyBrief }, "AI Programmer is designing your enemy roster…")}
              className="rounded-full gap-1.5 bg-gradient-to-r from-rose-500 to-violet-600">
              {busy === "enemies" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Swords className="w-4 h-4" />}
              {enemies.length ? "Add more" : "Design enemies"}
            </Button>
          </div>
          <input value={enemyBrief} onChange={(e) => setEnemyBrief(e.target.value)}
            placeholder="Optional brief — e.g. add a cloaking assassin and a shielded commander…"
            data-testid="gv2-enemy-brief"
            className="w-full rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-4 py-2 text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-violet-500/40" />
          {enemies.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-[hsl(var(--border))] p-8 text-center text-sm text-[hsl(var(--muted-foreground))]">
              No enemies designed yet. The AI Programmer will author distinct archetypes — flankers, snipers, heavies — each with a real behaviour tree and squad tactics.
            </div>
          ) : (
            <div className="space-y-2">
              {enemies.map((e) => {
                const bt = behaviourOf(e.id)?.spec || {};
                return (
                  <div key={e.id} className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4" data-testid={`gv2-enemy-card-${e.id}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-lg">{ARCHETYPE_EMOJI[e.spec?.archetype] || "👾"}</span>
                      <span className="font-bold truncate flex-1">{e.title}</span>
                      <Badge variant="outline" className="text-[10px] capitalize">{e.spec?.archetype || "enemy"}</Badge>
                      <Badge variant="outline" className={`text-[10px] ${TIER_STYLE[e.spec?.threat] || ""}`}>Threat {e.spec?.threat || 1}</Badge>
                    </div>
                    {e.spec?.role && <div className="text-xs text-[hsl(var(--muted-foreground))] mb-2">{e.spec.role}</div>}
                    {e.spec?.tactics?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-2">
                        {e.spec.tactics.slice(0, 4).map((t, i) => <span key={i} className="text-[10px] bg-[hsl(var(--muted))]/50 rounded px-1.5 py-0.5">{String(t)}</span>)}
                      </div>
                    )}
                    {bt.branches?.length > 0 && (
                      <div className="rounded-lg bg-[hsl(var(--muted))]/30 p-2 mt-1" data-testid={`gv2-behaviour-${e.id}`}>
                        <div className="text-[10px] font-bold uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-1 flex items-center gap-1"><GitBranch className="w-3 h-3" /> Behaviour tree · {bt.root || "Selector"}</div>
                        {bt.branches.slice(0, 5).map((b, i) => (
                          <div key={i} className="text-[11px] flex items-start gap-1.5">
                            <ChevronRight className="w-3 h-3 mt-0.5 text-violet-400 shrink-0" />
                            <span><span className="text-[hsl(var(--muted-foreground))]">if</span> {b.condition} <span className="text-[hsl(var(--muted-foreground))]">→</span> {b.action}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
